/**
 * Identifier of the currently loaded build, used to cache-bust assets that Angular
 * does not content-hash (translation files, in particular).
 *
 * Angular content-hashes the main bundle (`main-4KJ2QF7B.js`), and `index.html` is served with
 * `no-store`, so the script tags of the loaded document always name the current build. Reading
 * the hash back from those tags gives a value that changes whenever the application code
 * changes, without requiring a constant injected by the CI.
 *
 * Only `main` is used: esbuild propagates a lazy chunk's hash up to the modules that import it,
 * so any reachable code change moves the main hash, while `polyfills` is stable across most
 * deployments and would pin a stale value.
 *
 * When no hashed main bundle is present (`ng serve`, unit tests, hashing disabled) the value
 * falls back to a per-load timestamp, so a development build never pins a stale file either.
 */

const HASHED_MAIN_PATTERN = /\/main-([A-Za-z0-9]{6,})\.js(?:$|\?)/

const readHashedBundleId = (doc: Document): string | null => {
  const scripts = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]'))

  for (const script of scripts) {
    const match = HASHED_MAIN_PATTERN.exec(script.src)
    if (match) {
      return match[1]
    }
  }

  return null
}

let cachedBuildId: string | null = null

/**
 * Returns the identifier of the current build. The value is resolved once per page load.
 */
export const getBuildId = (): string => {
  if (cachedBuildId !== null) {
    return cachedBuildId
  }

  const doc = typeof document === 'undefined' ? null : document
  cachedBuildId = (doc && readHashedBundleId(doc)) ?? `dev-${Date.now()}`

  return cachedBuildId
}

/** Test seam: forces the next `getBuildId()` call to resolve again. */
export const resetBuildIdCache = () => {
  cachedBuildId = null
}
